import { useContext, useEffect, useState } from "react"

import { Accordion, AccordionDetails, AccordionSummary, Button, Checkbox, Container, FormControl, FormControlLabel, Radio, RadioGroup, Typography } from '@mui/material'
import { useTranslation } from "react-i18next"

import CommonBackButton from '../../../atoms/CommonBackButton/CommonBackButton'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import Spacer from "../../../atoms/Spacer/Spacer"
import { UserContext } from "../../../../stores/User/UserStore"

import { logLandingPageAction } from "../../../../clients/logger"
import useTrackPageView from "../../../../helpers/hooks/use-track-page-view"

import styles from './SubscriptionGiftDeclaration.module.css'
import SubscriptionGiftStepperStep from "../../../molecules/SubscriptionGiftStepper/SubscriptionGiftStepper"
import CustomInput from "../../../atoms/CustomInput/CustomInput"
import { MembershipStatus } from "../../../../types/MembershipStatus"
import CustomDatePicker from "../../../atoms/CustomDatePicker/CustomDatePicker"
import useGoogleTagManager from "../../../../helpers/analytics/use-google-tag-manager"
import GtmEventName from "../../../../helpers/analytics/GtmEventName"
import { Route } from "../../../../router"
import { useNavigate } from "react-router-dom"
import moment from "moment"
import { useMembershipRepository } from "../../../../repositories/Membership/use-membership-repository"
import SUBSCRIPTION_GIFT_FORM from "../../../../helpers/validation/rules/subscription-gift-form"
import { isValidMemberAge } from "../../../../helpers/validation/age"

const SubscriptionGiftDeclaration = () => {

    const { t } = useTranslation()
    const { trackButtonClick } = useGoogleTagManager()
    const { retrieveMembership } = useMembershipRepository()
    const { state: userState } = useContext(UserContext)
    const [nonMemberDeclaration, setNonMemberDeclaration] = useState(false)
    const [validSingpassDeclaration, setValidSingpassDeclaration] = useState(false)
    const [unionMemberDeclaration, setUnionMemberDeclaration] = useState(false)
    const [unionMemberReferralCode, setUnionMemberReferralCode] = useState(``)
    const [dateOfBirth, setDateOfBirth] = useState<Date | null>(null)
    const [validationError, setValidationError] = useState(false)
    const validationErrorMessage = `Please double check the form for errors`
    const [dobValidationError, setDobValidationError] = useState(false)
    const dobValidationErrorMessage = `Please enter a valid date of birth. The recipient must be between 18 and 24 years old to redeem this Subscription.`

    const navigate = useNavigate()

    const membershipStatus = userState.membership?.data?.status
    const userMembership = userState.membership?.data?.type

    useTrackPageView(`Subscription Gift Declaration Page`, `status: ${membershipStatus}, type: ${userMembership} `)

    useEffect(() => {
        if (!userState.membership?.data){
            retrieveMembership()
        }
        if (membershipStatus == MembershipStatus.UNION_ACTIVE){
            setUnionMemberDeclaration(true)
        }
    }, [])

    const areInputsValid = (): boolean => {
        try {
            SUBSCRIPTION_GIFT_FORM.unionMemberReferralCode.parse(unionMemberReferralCode)
            return !!dateOfBirth
            && !dobValidationError
            && nonMemberDeclaration
            && validSingpassDeclaration
            && ((unionMemberDeclaration && unionMemberReferralCode.length > 0) || !unionMemberDeclaration)
        } catch (error) {
            return false
        }
    }

    const handleSubmit = () => {
        if (areInputsValid()){
            setValidationError(false)
            trackButtonClick(GtmEventName.SUBSCRIPTION_GIFT_DECLARATION_SUBMIT)
            logLandingPageAction(`User pressed submit on Submission declaration`, `myInfo`)
            userState.subscriptionGift = {
                ...userState.subscriptionGift,
                unionMemberReferralCode,
                dateOfBirth: moment(dateOfBirth).format(`DDMMYYYY`),
            }
            const formattedDOB = moment(dateOfBirth).format(`DDMMYYYY`)
            navigate(`${Route.SUBSCRIPTION_GIFT_DETAILS_FORM}/${formattedDOB}`)
        }else{
            setValidationError(true)
        }
    }

    const handleDateChange = (value: Date | null) => {
        setDateOfBirth(value)
        setDobValidationError(false)
        setValidationError(false)
        if (!isValidMemberAge(value ?? new Date())){
            setDobValidationError(true)
        }
    }

    return (
        <Container>
            <CommonBackButton />
            <Spacer height={24} />
            <SubscriptionGiftStepperStep activeStep={0}/>
            <Spacer height={36} />
            <Typography variant="h3">{t(`declaration`)}</Typography>
            <Container className={styles.container}>
                <Checkbox checked={nonMemberDeclaration} onChange={() => setNonMemberDeclaration(!nonMemberDeclaration)} color='functional' />
                <Typography variant="h6">
                    {t(`declaration_non_member`)}
                </Typography>
            </Container>
            <Container className={styles.container}>
                <Checkbox checked={validSingpassDeclaration} onChange={() => setValidSingpassDeclaration(!validSingpassDeclaration)} color='functional' />
                <Typography variant="h6">
                    {t(`declaration_valid_singpass`)}
                </Typography>
            </Container>
            <Spacer height={24} />
            <CustomDatePicker
                fullWidth
                label={t(`declaration_date_of_birth`)}
                onChange={handleDateChange}
                value={dateOfBirth}
            />
            {dobValidationError && <Typography className={styles.validationError}>{dobValidationErrorMessage}</Typography>}
            {membershipStatus !== MembershipStatus.ACTIVE &&
            <>
                <Spacer height={24} />
                <Typography variant="h3">{t(`declaration_union_member`)}</Typography>
                <FormControl>
                    <Spacer height={24} />
                    <RadioGroup
                        className={styles.radioGroup}
                        row
                        value={unionMemberDeclaration}
                        onChange={() => setUnionMemberDeclaration(!unionMemberDeclaration)}>
                        <FormControlLabel value={true} control={<Radio />} label="Yes" />
                        <FormControlLabel value={false} control={<Radio />} label="No" />
                    </RadioGroup>
                </FormControl>
                {unionMemberDeclaration &&
                <>
                    <Spacer height={24} />
                    <CustomInput
                        fullWidth
                        label={t(`declaration_union_member_referral_code_label`)}
                        onChange={setUnionMemberReferralCode}
                        eagerChange
                        placeholder={t(`common_placeholder`)}
                        validation={SUBSCRIPTION_GIFT_FORM.unionMemberReferralCode}
                        value={unionMemberReferralCode}
                        isSignUp={true} />
                    <Accordion elevation={0} disableGutters sx={{'&:before': {display: `none`}}}>
                        <AccordionSummary aria-controls="panel2d-content" id="panel2d-header" expandIcon={<ArrowDropDownIcon />} className={styles.accordionSummary}>
                            <Typography variant="h6" className={styles.accordionText}>{t(`declaration_union_member_referral_code_instructions`)}</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <img className={styles.accordionImg} src="https://itdstarterappsaccblobsea.blob.core.windows.net/strapi-uploads-prod/assets/Image_Resource_Referral_Sign_up_for_Starter_Gift_Dropdown_83ba84d641.png" alt="Union member referral code instructions" />
                        </AccordionDetails>
                    </Accordion >
                </>
                }
            </>
            }
            {validationError && <Typography className={styles.validationError}>{validationErrorMessage}</Typography>}
            <Spacer height={24} />
            <Button variant='contained' onClick={handleSubmit} fullWidth>{t(`submit`)}</Button>
        </Container>
    )
}

export default SubscriptionGiftDeclaration